import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { GanttData, Loom, WarpBeam, ClothPiece } from '../types';
import { eachDayOfInterval, format, differenceInMilliseconds } from 'date-fns';
import { it } from 'date-fns/locale';
import { NoteIcon } from './NoteIcon';
import { DischargeIcon } from './DischargeIcon';

interface GanttChartProps {
    data: GanttData;
    looms: Loom[];
    matchingLoomIds: Set<string> | null;
    matchingBeamIds?: Set<string> | null;
    matchingPieceIds?: Set<string> | null;
    dateRange: { start: Date; end: Date };
    showTooltip: (content: React.ReactNode, pos: { x: number; y: number }) => void;
    hideTooltip: () => void;
}

const WPB_STATUS_COLORS: Record<WarpBeam['status'], { bgColor: string, textColor: string, statusClass: string }> = {
    D: { bgColor: 'bg-[#FF3300]', textColor: 'text-white', statusClass: '.StD' }, // Disposto/Prossimo
    L: { bgColor: 'bg-[#3366FF]', textColor: 'text-white', statusClass: '.StL' }, // Lavoro
    V: { bgColor: 'bg-gray-700', textColor: 'text-white', statusClass: '.StV' }, // Virtuale
    T: { bgColor: 'bg-[#A0A0A0]', textColor: 'text-white', statusClass: '.StT' }, // Terminato/Precedente
};

const CP_STATUS_COLORS: Record<ClothPiece['status'], { bgColor: string, textColor: string, statusClass: string }> = {
    D: { bgColor: 'bg-[#FF00CC]', textColor: 'text-white', statusClass: '.StD' }, // Prossima pezza
    L: { bgColor: 'bg-[#33FFCC]', textColor: 'text-black', statusClass: '.StL' }, // Pezza Attuale
    P: { bgColor: 'bg-[#C0C0C0]', textColor: 'text-black', statusClass: '.StP' }, // Pezza Precedente
    Fin: { bgColor: 'bg-[#C09030]', textColor: 'text-white', statusClass: '.StFin' }, // Finissaggio
};

// Larghezza di un giorno in pixel: la larghezza totale del grafico dipende dall'intervallo di date
const PX_PER_DAY = 32;

const dateToPercentage = (date: Date, rangeStart: Date, totalDuration: number) => {
    const offset = differenceInMilliseconds(date, rangeStart);
    return (offset / totalDuration) * 100;
};

const getLoomRowHeight = (loom: Loom) => {
    return loom.totalSubbi === 2 ? 'h-[80px]' : 'h-[40px]';
};

const TooltipContentLoom: React.FC<{ loom: Loom; warpBeams: WarpBeam[]; clothPiecesByWarpBeam: Record<string, ClothPiece[]> }> = ({ loom, warpBeams, clothPiecesByWarpBeam }) => {
    const activeBeam = warpBeams.find(wb => wb.status === 'L');
    const activePiece = activeBeam ? (clothPiecesByWarpBeam[activeBeam.id] || []).find(p => p.status === 'L') : undefined;
    return (
        <div className="text-xs min-w-[180px]">
            <div className="text-center font-bold border-b pb-1 mb-2 text-sm">{loom.name}</div>
            <table>
                <tbody>
                    <tr><td className="pr-2 font-semibold">Tipo:</td><td>{loom.type}</td></tr>
                    <tr><td className="pr-2 font-semibold">Calendario:</td><td>{loom.calendarId}</td></tr>
                    <tr><td className="pr-2 font-semibold">Subbi:</td><td>{loom.totalSubbi}</td></tr>
                    <tr><td className="pr-2 font-semibold">Inizio:</td><td>{format(loom.start, 'dd/MM/yy HH:mm')}</td></tr>
                    <tr><td className="pr-2 font-semibold">Fine:</td><td>{format(loom.end, 'dd/MM/yy HH:mm')}</td></tr>
                    <tr><td className="pr-2 font-semibold">Metri:</td><td>{loom.metersNow}</td></tr>
                    {loom.status.description && (
                        <tr>
                            <td className="pr-2 font-semibold align-top">Stato:</td>
                            <td><span className="font-bold" style={{ color: loom.status.color }}>{loom.status.description}</span></td>
                        </tr>
                    )}
                    {activePiece && (
                        <>
                            <tr><td colSpan={2} className="pt-2 pb-0.5 font-bold border-t mt-1">Articolo attuale</td></tr>
                            <tr><td className="pr-2 font-semibold">Articolo:</td><td>{activePiece.articleBase} <span className="text-gray-400">({activePiece.article})</span></td></tr>
                            <tr><td className="pr-2 font-semibold">Pezza:</td><td>{activePiece.group}</td></tr>
                            <tr><td className="pr-2 font-semibold">Metri:</td><td><b>{activePiece.metersStart}</b>/{activePiece.metersTotal}</td></tr>
                            <tr><td className="pr-2 font-semibold">Flangia:</td><td>{activePiece.flange}</td></tr>
                            {activePiece.annodatura && <tr><td className="pr-2 font-semibold">Annodatura:</td><td>{activePiece.annodatura}</td></tr>}
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );
};

const TooltipContentWarpBeam: React.FC<{ beam: WarpBeam }> = ({ beam }) => (
    <div className="text-xs">
        <div className="text-center font-bold border-b pb-1 mb-1">
            {formatDateTimeWithWeekday(beam.start)} &rarr; {formatDateTimeWithWeekday(beam.end)}
        </div>
        <div className="text-center text-gray-500 mb-2">
            Durata: {formatDuration(differenceInMilliseconds(beam.end, beam.start))}
        </div>
        <table>
            <tbody>
                <tr><td className="pr-2 font-semibold">Ordito:</td><td>{beam.ordito}</td></tr>
                <tr><td className="pr-2 font-semibold">Num Subbio:</td><td>{beam.sb}</td></tr>
                <tr><td className="pr-2 font-semibold">Metri:</td><td><b>{beam.metersStart}</b> / {beam.metersTotal}</td></tr>
            </tbody>
        </table>
    </div>
);

const TooltipContentClothPiece: React.FC<{ piece: ClothPiece }> = ({ piece }) => (
    <div className="text-xs">
        <div className="text-center font-bold border-b pb-1 mb-1">
            {formatDateTimeWithWeekday(piece.start)} &rarr; {formatDateTimeWithWeekday(piece.end)}
        </div>
        <div className="text-center text-gray-500 mb-2">
            Durata: {formatDuration(differenceInMilliseconds(piece.end, piece.start))}
        </div>
        <table>
            <tbody>
                <tr><td className="pr-2 font-semibold">Articolo:</td><td>{piece.articleBase} <span className="text-gray-500 text-xs">({piece.article})</span></td></tr>
                <tr><td className="pr-2 font-semibold">Num Pezza:</td><td><b>{piece.group}</b></td></tr>
                <tr><td className="pr-2 font-semibold">Metri:</td><td><b>{piece.metersStart}</b> / {piece.metersTotal}</td></tr>
                <tr><td className="pr-2 font-semibold">Annodatura:</td><td><b>{piece.annodatura}</b></td></tr>
                <tr><td className="pr-2 font-semibold">Flangia:</td><td><b>{piece.flange}</b></td></tr>
                {piece.notes.caricaSubbi && <tr><td className="pr-2 font-semibold">Nota CS:</td><td>{piece.notes.caricaSubbi}</td></tr>}
                {piece.notes.speciale && <tr><td className="pr-2 font-semibold">Nota Spec:</td><td>{piece.notes.speciale}</td></tr>}
                {piece.notes.portaTrama && <tr><td className="pr-2 font-semibold">Nota PT:</td><td>{piece.notes.portaTrama}</td></tr>}
                {piece.dischargeDate && <tr><td className="pr-2 font-semibold">Scaricato:</td><td>{format(piece.dischargeDate, 'dd/MM HH:mm')} {piece.dischargeOperator && ` da ${piece.dischargeOperator}`}</td></tr>}
            </tbody>
        </table>
    </div>
);


const formatDuration = (ms: number) => {
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    return `${days}g ${hours}h ${minutes}m`;
};

const WEEKDAY_SHORT_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const formatDateTimeWithWeekday = (date: Date) => `${WEEKDAY_SHORT_IT[date.getDay()]} ${format(date, 'dd/MM/yy HH:mm')}`;


const ADJACENCY_TOLERANCE_MS = 2 * 60 * 1000; // 2 minuti di tolleranza

const getAdjacentRounding = (start: Date, end: Date, pos: number, siblings: { start: Date; end: Date; parallelPosition: number }[]) => {
    const same = siblings.filter(s => s.parallelPosition === pos);
    const hasLeft  = same.some(s => Math.abs(s.end.getTime()   - start.getTime()) <= ADJACENCY_TOLERANCE_MS);
    const hasRight = same.some(s => Math.abs(s.start.getTime() - end.getTime())   <= ADJACENCY_TOLERANCE_MS);
    if (hasLeft && hasRight) return '';
    if (hasLeft)  return 'rounded-r-sm';
    if (hasRight) return 'rounded-l-sm';
    return 'rounded-sm';
};

export const GanttChart = forwardRef<HTMLDivElement, GanttChartProps>(({ data, looms, matchingLoomIds, matchingBeamIds, matchingPieceIds, dateRange, showTooltip, hideTooltip }, ref) => {
    const scrollTelRef = useRef<HTMLDivElement>(null);
    const scrollDtRef = useRef<HTMLDivElement>(null);
    const scrollSubRef = useRef<HTMLDivElement>(null);
    
    useImperativeHandle(ref, () => scrollSubRef.current as HTMLDivElement);

    const totalDuration = differenceInMilliseconds(dateRange.end, dateRange.start);
    const chartWidth = Math.round((totalDuration / 86400000) * PX_PER_DAY);

    // La linea "Adesso" si aggiorna ogni minuto anche senza altri render
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(id);
    }, []);

    // Props comuni per gli elementi con tooltip: mouse, tocco (click) e tastiera (focus)
    const tipProps = (content: React.ReactNode, label: string) => ({
        tabIndex: 0,
        'data-tip': true,
        'aria-label': label,
        onMouseMove: (e: React.MouseEvent) => showTooltip(content, { x: e.pageX, y: e.pageY }),
        onMouseLeave: hideTooltip,
        onClick: (e: React.MouseEvent) => showTooltip(content, { x: e.pageX, y: e.pageY }),
        onFocus: (e: React.FocusEvent<HTMLElement>) => {
            const r = e.currentTarget.getBoundingClientRect();
            showTooltip(content, { x: r.left + window.scrollX + 8, y: r.bottom + window.scrollY });
        },
        onBlur: hideTooltip,
    });
    const focusClass = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500';

    const days = eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
    const months: { name: string; start: Date; end: Date }[] = [];
    days.forEach(day => {
        const monthName = format(day, 'MMMM yyyy', { locale: it });
        if (months.length === 0 || months[months.length - 1].name !== monthName) {
            months.push({ name: monthName, start: day, end: day });
        } else {
            months[months.length - 1].end = day;
        }
    });

    const handleScroll = useCallback(() => {
        if (scrollSubRef.current && scrollTelRef.current && scrollDtRef.current) {
            scrollTelRef.current.scrollTop = scrollSubRef.current.scrollTop;
            scrollDtRef.current.scrollLeft = scrollSubRef.current.scrollLeft;
        }
    }, []);

    const nowPercentage = dateToPercentage(now, dateRange.start, totalDuration);

    return (
        <div className="relative w-full h-full">
            {/* Date Header */}
            <div id="ScrollDt" ref={scrollDtRef} className="absolute top-0 left-[51px] right-0 h-[30px] border border-gray-300 overflow-hidden z-20">
                 <div id="DateBlkUP" className="relative h-full bg-[#6699FF]" style={{ width: chartWidth }}>
                     {months.map(month => {
                         const left = dateToPercentage(month.start, dateRange.start, totalDuration);
                         const right = dateToPercentage(new Date(month.end.getTime() + 86400000), dateRange.start, totalDuration);
                         return (
                             <div key={month.name} className="absolute top-0 h-1/2 flex items-center justify-center font-bold capitalize text-yellow-200 border-l border-dashed border-yellow-300/60" style={{ left: `${left}%`, width: `${right - left}%`, background: 'linear-gradient(to right, #3AABB8, #66CCCC, #3AABB8)' }}>
                                 {month.name}
                             </div>
                         );
                     })}
                     {days.map(day => {
                         const isSunday = day.getDay() === 0;
                         const isNonWorking = data.calendar[format(day, 'yyyy-MM-dd')]?.isNonWorking;
                         let bgColor = '';
                         if(isSunday) bgColor = 'bg-red-200/50';
                         if(isNonWorking) bgColor = 'bg-gray-300/50';

                         return (
                             <div key={day.toISOString()} className={`absolute bottom-0 h-1/2 flex items-center justify-center font-bold text-sm border-l border-dashed border-yellow-300 ${bgColor}`} style={{ left: `${dateToPercentage(day, dateRange.start, totalDuration)}%`, width: `${(86400000 / totalDuration) * 100}%` }}>
                                 {format(day, 'd')}
                             </div>
                         );
                     })}
                 </div>
            </div>

            {/* Loom Labels */}
            <div id="ScrollTel" ref={scrollTelRef} className="absolute top-[30px] left-0 w-[50px] h-full border border-gray-300 overflow-hidden bg-gray-100 z-20">
                <div id="Telai" className="relative w-full">
                    {looms.map(loom => (
                        <div
                            key={loom.id}
                            id={`tel-${loom.id}`}
                            className={`relative flex flex-col items-center justify-center text-center border-b border-black ${getLoomRowHeight(loom)} transition-opacity duration-200 cursor-pointer ${focusClass} ${matchingLoomIds && !matchingLoomIds.has(loom.id) ? 'opacity-20' : ''}`}
                            {...tipProps(<TooltipContentLoom loom={loom} warpBeams={data.warpBeamsByLoom[loom.id] || []} clothPiecesByWarpBeam={data.clothPiecesByWarpBeam} />, `Telaio ${loom.name}${loom.status.description ? `: ${loom.status.description}` : ''}`)}
                        >
                            <b className={loom.totalSubbi === 2 ? 'text-sm' : 'text-[11px] leading-tight'}>{loom.name.substring(0, 1)} {loom.name.substring(1)}</b>
                            <small className={loom.totalSubbi === 2 ? 'text-xs text-gray-500' : 'text-[9px] text-gray-500 leading-tight'}>{loom.calendarId}</small>
                            <div
                                className={loom.totalSubbi === 2 ? 'absolute bottom-px left-[20px] w-5 h-5 border border-gray-400' : 'absolute bottom-px right-px w-3 h-3 border border-gray-400'}
                                style={{ backgroundColor: loom.status.color || 'transparent' }}
                            ></div>
                        </div>
                    ))}
                </div>
            </div>
            
            {/* Gantt Body */}
            <div id="ScrollSub" ref={scrollSubRef} onScroll={handleScroll} className="absolute top-[30px] left-[51px] right-0 bottom-0 border border-gray-300 overflow-auto">
                <div id="GanttZone" className="relative min-h-full bg-white overflow-hidden" style={{ width: chartWidth }}>
                    {/* Day Backgrounds */}
                    {days.map(day => {
                        const isSunday = day.getDay() === 0;
                        const isNonWorking = data.calendar[format(day, 'yyyy-MM-dd')]?.isNonWorking;
                        let clas = '';
                        if (isSunday) clas = 'bg-[#8097A0]/30'; // domenica
                        if (isNonWorking) clas = 'bg-[#A0B7E0]/30'; // nonLavora
                        if (!clas) return null;
                        return (
                            <div key={day.toISOString()} className={`absolute top-0 bottom-0 ${clas}`} style={{ left: `${dateToPercentage(day, dateRange.start, totalDuration)}%`, width: `${(86400000 / totalDuration) * 100}%`}}></div>
                        )
                    })}

                    {/* Loom Rows */}
                    {looms.map(loom => {
                        const warpBeams = data.warpBeamsByLoom[loom.id] || [];
                        return (
                            <div key={loom.id} id={`row-${loom.id}`} className={`relative border-b border-black ${getLoomRowHeight(loom)} transition-opacity duration-200 ${matchingLoomIds && !matchingLoomIds.has(loom.id) ? 'opacity-20' : ''}`}>
                                {loom.totalSubbi === 2 && <div className="absolute top-1/2 w-full h-0 border-t border-dashed border-gray-500 z-0"></div>}
                            
                                {/* Warp Beams */}
                                {warpBeams.map(beam => {
                                    const left = dateToPercentage(beam.start, dateRange.start, totalDuration);
                                    const width = dateToPercentage(beam.end, dateRange.start, totalDuration) - left;
                                    const top = beam.parallelPosition === 1 ? '5px' : '45px';
                                    const { bgColor, textColor } = WPB_STATUS_COLORS[beam.status];
                                    
                                    const clothPieces = data.clothPiecesByWarpBeam[beam.id] || [];
                                    
                                    const beamRounding = getAdjacentRounding(beam.start, beam.end, beam.parallelPosition, warpBeams.filter(b => b.id !== beam.id));

                                    return (
                                        <React.Fragment key={beam.id}>
                                            <div
                                                className={`absolute h-[14px] border border-gray-700 ${beamRounding} gantt-shadow-bar-strong text-[10px] overflow-hidden whitespace-nowrap px-1 z-10 ${bgColor} ${textColor} cursor-pointer transition-opacity duration-200 ${focusClass} ${matchingBeamIds && !matchingBeamIds.has(beam.id) ? 'opacity-20' : ''}`}
                                                style={{ left: `${left}%`, width: `${width}%`, top }}
                                                {...tipProps(<TooltipContentWarpBeam beam={beam} />, `Subbio ${beam.ordito} ${beam.sb}, telaio ${loom.name}`)}
                                            >
                                                &nbsp;{beam.ordito}; {beam.sb}
                                            </div>

                                            {/* Cloth Pieces */}
                                            {clothPieces.map(piece => {
                                                const pleft = dateToPercentage(piece.start, dateRange.start, totalDuration);
                                                const pwidth = dateToPercentage(piece.end, dateRange.start, totalDuration) - pleft;
                                                const ptop = piece.parallelPosition === 1 ? '20px' : '60px';
                                                const pcolors = CP_STATUS_COLORS[piece.status];
                                                const hasNotes = piece.notes.caricaSubbi || piece.notes.speciale || piece.notes.portaTrama;

                                                const pieceRounding = getAdjacentRounding(piece.start, piece.end, piece.parallelPosition, clothPieces.filter(p => p.id !== piece.id));

                                                return (
                                                    <React.Fragment key={piece.id}>
                                                        <div
                                                            className={`flex items-center absolute h-[14px] border border-gray-600 ${pieceRounding} gantt-shadow-bar text-[10px] overflow-hidden whitespace-nowrap px-1 z-10 ${pcolors.bgColor} ${pcolors.textColor} cursor-pointer transition-opacity duration-200 ${focusClass} ${matchingPieceIds && !matchingPieceIds.has(piece.id) ? 'opacity-20' : ''}`}
                                                            style={{ left: `${pleft}%`, width: `${pwidth}%`, top: ptop }}
                                                            {...tipProps(<TooltipContentClothPiece piece={piece} />, `Pezza ${piece.articleBase} ${piece.group}, telaio ${loom.name}`)}
                                                        >
                                                            {hasNotes && <NoteIcon className="inline-block mr-1" />}
                                                            <span>{piece.articleBase}; {piece.group}; Mt:<b>{piece.metersStart}</b>/{piece.metersTotal}; {piece.flange}</span>
                                                        </div>
                                                        {piece.dischargeDate && (
                                                            <div className="absolute z-20" style={{ left: `${dateToPercentage(piece.dischargeDate, dateRange.start, totalDuration)}%`, top: '40%', transform: 'translateY(-50%)' }}>
                                                                <DischargeIcon title={`Scaricato ${format(piece.dischargeDate, 'dd/MM HH:mm')}`} />
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )
                    })}

                    {/* "Now" Line */}
                    {nowPercentage > 0 && nowPercentage < 100 && (
                         <div id="Adesso" className="absolute top-0 bottom-0 w-0.5 bg-blue-600 opacity-60 z-30 pointer-events-none" style={{ left: `${nowPercentage}%` }}></div>
                    )}
                </div>
            </div>
        </div>
    );
});
